/**
 * One graph, laid out in the style the player is looking at (D209).
 *
 * @remarks
 * Until D209 the play host chose the style and put the DRAWING on the wire:
 * a session view carried a `basic` or a `holo` layout, and a player could
 * see only the map the host had laid out for them. D209 puts the GRAPH on
 * the wire and lays it out where it is drawn, so the style is the player's
 * choice among the styles the pack offers, and the choice costs no round
 * trip. This module is that choice as a function: the style-discriminated
 * union a renderer switches on, and the one place that knows which layout
 * builder each style means.
 *
 * Isomorphic on purpose — the same function runs in the browser (the
 * dialog, in whichever style the player chose), in a local host's tests and
 * in the golden pins of both layouts, so nothing can lay a graph out two
 * ways. Each flavor of graph — flat for the plan, levelled for the hologram
 * — is derived by `deriveMapGraph` where the state is (the host); which
 * flavor a style needs is {@link mapGraphFlavorOf}, kept beside the layouts
 * because it is the same fact read from the other side, and the host asks
 * it to know which graphs to send.
 *
 * @packageDocumentation
 */

import type { MapStyle } from '@orpheus/content-schema';

import type { MapGraph } from './map-graph.js';

import { buildMapHoloLayout, type MapHoloLayout, type MapHoloLayoutOptions } from './map-holo-layout.js';
import { buildMapSceneLayout, type MapSceneLayout } from './map-scene-layout.js';
import { buildMapSvgLayout, type MapSvgLayout } from './map-svg-layout.js';

/**
 * The two derivations of the graph (D169): FLAT places every room on one
 * floor and annotates `up`/`down`; LEVELLED places a vertical exit's room
 * one floor away and draws the lift. A style needs exactly one of them —
 * the plan the flat one, the hologram and the scene the levelled one.
 */
export type MapGraphFlavor = 'flat' | 'levelled';

/**
 * What a map is DRAWN as (D215): one of the builder's two styles, or the
 * hologram — which is not a style a builder offers or a player chooses
 * since D215 (Mike, 2026-09-27), only what `scene` is drawn as where the
 * browser cannot draw a scene. The wire and the chooser speak in styles;
 * the layouts and the figures speak in drawings, because the stand-in is a
 * real drawing with a real layout and the type has to say so.
 */
export type MapDrawing = MapStyle | 'holo';

/** The map, laid out for drawing, discriminated on the drawing it was laid out as. */
export type MapLayout =
	| ({ readonly style: 'basic' } & MapSvgLayout)
	| ({
		readonly style: 'holo';
		/** The pack's tint for the material, when it chose one; absent, the brand's. */
		readonly tint?: string;
	} & MapHoloLayout)
	| ({
		readonly style: 'scene';
		/** The pack's tint for the material, when it chose one; absent, the brand's. */
		readonly tint?: string;
	} & MapSceneLayout);

/**
 * Which flavor of graph a drawing reads.
 *
 * @param style - The map style
 * @returns The graph flavor its layout reads
 */
export const mapGraphFlavorOf = (drawing: MapDrawing): MapGraphFlavor => (drawing === 'basic' ? 'flat' : 'levelled');

/** What the drawing's host knows that the graph does not (D224) — today, only what the hologram asks: whether the figure is narrow. */
export type LayOutMapGraphOptions = MapHoloLayoutOptions;

/**
 * Lay a graph out as a drawing.
 *
 * @param graph - The graph, in the flavor the drawing needs ({@link mapGraphFlavorOf})
 * @param drawing - What to lay it out as
 * @param tint - The pack's tint for the hologram's or the scene's material, if it chose one; ignored by the plan
 * @param options - What the host knows; read by the hologram, ignored by the plan and the scene (whose narrow answer is the stylesheet's)
 * @returns The layout, tagged with its drawing
 */
export const layOutMapGraph = (graph: MapGraph, drawing: MapDrawing, tint?: string, options: LayOutMapGraphOptions = {}): MapLayout => {
	switch (drawing) {
		case 'basic':
			return { ...buildMapSvgLayout(graph), style: drawing };
		case 'holo':
			return { ...buildMapHoloLayout(graph, options), style: drawing, ...(tint === undefined ? {} : { tint }) };
		case 'scene':
			return { ...buildMapSceneLayout(graph), style: drawing, ...(tint === undefined ? {} : { tint }) };
	}
};
